import React, { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { ActivityIndicator, Alert, useConfirmationModal } from 'LocalGluons';
import { getAssetTitle, getTimeStringFromTimestamp, isLiveTv } from 'Utils';
import { useStore } from 'Store';
import { Device } from 'DeviceUtils';
import { getRecordingForItem, hasRecordingData } from 'Utils/itemUtils';
import { isUserFreemium } from 'Utils/userUtils';
import { TITLE_NUMBERING_EPISODE } from '@24i/st-api-connector/utils';
import { PlayerManager } from '@24i/react-native-player';
import {
    SeekType,
    type IAudioTrack,
    type ITextTrack,
    ISourceWithoutDRMProvider,
    CASTING_TYPE
} from '@24i/player-base';
import type { PlayerData, StreamType, StyleMap, TimeInS } from 'Types';
import { modelType } from '@24i/st-api-connector';
import { t } from 'Locale';
import { logEvent } from 'AppManager';
import { useThumbnails } from 'LocalGluons/components/PlayerContainer/hooks/useThumbnails';
import { useChromecastContext } from 'LocalGluons/components/ChromecastContext';
import { playerModalsEnum } from '../PlayerModal/PlayerModal';
import TvCastOverlay from '../TvCastOverlay';
import PlayerControls from '../PlayerControls';
import PlayerFooter from '../PlayerFooter';
import PlayerHeader from '../PlayerHeader';
import Countdown from '../Countdown';
import { HIDE_PLAYER_UI_TIMEOUT, STREAM_TYPE } from '../../../../../options';
import GestureHandler from '../GestureHandler';
import defaultStyles from './PlayerOverlay.jss';
import useVolume from '../GestureHandler/hooks/useVolume';
// import useBrightness from '../GestureHandler/hooks/useBrightness';
import { GESTURE_DIRECTIONS, GesturesData } from '../GestureHandler/GestureHandler';
import { AdSkipPolicy } from '../../../PlayerContainer/hooks/useAdvertisements';
import useCanSeek from '../PlayerControls/hooks/useCanSeek';
import styles from '../../styles';

const HORIZONTAL_GESTURE_TIMEOUT = 300;

const whitelistedModals = [playerModalsEnum.recording, playerModalsEnum.settings];

interface PlayerOverlayProps {
    item: PlayerData['item'];
    liveItem: PlayerData['item'];
    onModalOpenRequest: (type: playerModalsEnum) => void;
    playerInstance: PlayerManager;
    streamType: StreamType;
    onResumePlaying: () => void;
    loading: boolean;
    onBack: () => void;
    onDurationPress: () => void;
    onCurrentTimePress: () => void;
    // Overlaying modal is opened
    modalOpened: boolean;
    // Type of overlaying modal; can be set event if it is opened or not
    modalType: playerModalsEnum | null;
    setModalType: (type: playerModalsEnum | null) => void;
    textTracks: ITextTrack[];
    audioTracks: IAudioTrack[];
    onMiniPlayerPress: () => void;
    isCasting: boolean;
    isPaused: boolean;
    selectedAudioTrack: IAudioTrack | null;
    selectedTextTrack: ITextTrack | null;
    currentTime?: number;
    maxTimeshift?: number;
    switchToLive: () => void;
    onHorizontalGesture: (gestureDirection: number) => void | undefined;
    onNextPress?: () => void;
    onPreviousPress?: () => void;
    archiveAvailable: boolean;
    setAlertMessage: React.Dispatch<SetStateAction<string>>;
    gracePeriodInSeconds: TimeInS | null;
    setProgress: any;
    progress: any;
    showActivityIndicator: boolean;
    setModalDisplay: React.Dispatch<React.SetStateAction<'flex' | 'none'>>;
    isModalBinge: boolean;
    setLiveLoading: React.Dispatch<React.SetStateAction<boolean>>;
    adSkipPolicy: AdSkipPolicy | null;
    switchToTimeshift: (timeshift?: number, startTimeType?: SeekType) => void;
    source: ISourceWithoutDRMProvider;
    scale: number;
    setScale: () => void;
    lowLatency: boolean;
    alertMessage: string;
    onPlay: () => void;
    onPause: () => void;
    mediaHouse: string | null;
}

// On large devices, UI should be displayed because some
// of the modals requires it (small overlay boxes above UI)
const shouldUiBePermanentlyDisplayed = (modalType: playerModalsEnum | null, modalOpened: boolean) =>
    modalType && !Device.isPhone && whitelistedModals.includes(modalType) && modalOpened;

// eslint-disable-next-line max-statements
const PlayerOverlay = ({
    item,
    liveItem,
    onModalOpenRequest,
    streamType,
    loading,
    onBack,
    onDurationPress,
    onCurrentTimePress,
    modalOpened,
    modalType,
    setModalType,
    textTracks,
    audioTracks,
    onMiniPlayerPress,
    isCasting,
    isPaused,
    selectedAudioTrack,
    selectedTextTrack,
    currentTime,
    onResumePlaying,
    maxTimeshift,
    switchToLive,
    onHorizontalGesture: onHorizontalGestureCallback,
    playerInstance: player,
    onNextPress,
    onPreviousPress,
    archiveAvailable,
    setAlertMessage,
    gracePeriodInSeconds,
    setProgress,
    progress,
    showActivityIndicator,
    setModalDisplay,
    isModalBinge,
    setLiveLoading,
    adSkipPolicy,
    switchToTimeshift,
    source,
    scale,
    setScale,
    lowLatency,
    alertMessage,
    onPlay: onPlayPress,
    onPause: onPausePress,
    mediaHouse
}: PlayerOverlayProps): JSX.Element => {
    const { setMediaState, client, castDevice, castSession } = useChromecastContext();
    const playerInstance = player; // fixes eslint issue
    const [isVisible, setVisible] = useState(true);
    const [modal, setModal] = useState(null);
    const [modalContent, setModalContent] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const { userData, progressInfo, isGracePeriod } = useStore();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { volume, setVolume } = useVolume(playerInstance);
    // const { brightness, setBrightness } = useBrightness();
    const [longPauseFromLive, setLongPauseFromLive] = useState<number | null>(null);

    // ref
    const postoponeUiVisibilityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onHorizontalGestureTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const alertTimeout = useRef<NodeJS.Timeout>();
    const hasToPostponeVisibilityToggle = useRef(false);
    const visibilityToggleIsLocked = useRef(false);
    const lastTap = useRef(0);
    const { refreshThumbnails } = useThumbnails();

    const canSkipAd = adSkipPolicy === AdSkipPolicy.SKIPPABLE || isGracePeriod
        ? true : playerInstance?.canSkipAd;
    const currentAd = playerInstance?.currentAd;

    const { canSeekBackwards, canSeekForwards } = useCanSeek(
        playerInstance,
        streamType,
        currentTime || 0,
        currentAd ? canSkipAd : true
    );

    const postponeVisibilityToggle = useCallback(() => {
        clearTimeout(postoponeUiVisibilityTimeout.current);
        hasToPostponeVisibilityToggle.current = true;
        postoponeUiVisibilityTimeout.current = setTimeout(() => {
            hasToPostponeVisibilityToggle.current = false;
            postoponeUiVisibilityTimeout.current = null;
        }, HIDE_PLAYER_UI_TIMEOUT);
    }, []);

    const showUI = useCallback(() => {
        postponeVisibilityToggle();
        setVisible(true);
    }, [setVisible]);

    const toggleUI = useCallback(() => {
        if (
            isVisible &&
            modalType &&
            !isModalBinge &&
            shouldUiBePermanentlyDisplayed(modalType, modalOpened)
        ) {
            return;
        }
        if (isModalBinge) {
            setModalDisplay((display) => (display === 'flex' ? 'none' : 'flex'));
        }

        if (!isVisible) {
            postponeVisibilityToggle();
        }

        setVisible(!isVisible);
    }, [isVisible, modalOpened, modalType]);

    // WEB
    const toggleFullscreen = useCallback(() => {
        const playerDOMElement = document.getElementById('player');

        if (playerDOMElement) {
            if (fullscreen) {
                document.exitFullscreen();
            } else if (playerDOMElement?.requestFullscreen) {
                playerDOMElement.requestFullscreen();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore -- Mozilla prefix
            } else if (playerDOMElement?.mozRequestFullScreen) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                playerDOMElement.mozRequestFullScreen();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore -- WebKit prefix
            } else if (playerDOMElement?.webkitRequestFullscreen) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                playerDOMElement.webkitRequestFullscreen();
            }
            setFullscreen(!fullscreen);
        }
    }, [fullscreen]);

    const onVideoDoubleTap = () => {
        toggleFullscreen();
    };

    const togglePlayPause = useCallback(() => {
        postponeVisibilityToggle();
        if (playerInstance) {
            if (playerInstance.paused) {
                playerInstance.play();
                if (client) {
                    setMediaState((state) => ({
                        ...state,
                        isPaused: false
                    }));
                    client.play();
                }
            } else {
                playerInstance.pause();
                if (client) {
                    setMediaState((state) => ({
                        ...state,
                        isPaused: true
                    }));
                    client.pause();
                }
            }
        }
    }, [playerInstance]);

    const onOverlayPress = useCallback(() => {
        if (!modalContent) {
            if (Device.isWeb) {
                // Handle UI click
                showUI();

                // Handle double tap
                const now = Date.now();
                const DOUBLE_PRESS_DELAY = 300;
                if (lastTap.current && now - lastTap.current < DOUBLE_PRESS_DELAY) {
                    onVideoDoubleTap();
                } else {
                    lastTap.current = now;
                }

                return;
            }
            if (alertMessage) {
                hideAlertMessage();
                return;
            }
            toggleUI();
        }
    }, [toggleUI, showUI, alertMessage]);

    const onInfoPress = useCallback(() => {
        onModalOpenRequest(playerModalsEnum.detail);
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'info-button',
            context: '/player'
        });
    }, [onModalOpenRequest]);

    const onTvGuidePress = () => {
        onModalOpenRequest(playerModalsEnum.miniEpg);
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'mini-epg-opened',
            context: '/player'
        });
    };

    const onSettingsPress = () => {
        onModalOpenRequest(playerModalsEnum.settings);
    };

    const onLanguagePress = () => {
        onModalOpenRequest(playerModalsEnum.language);
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'ma-sub-option',
            context: '/player/ma-sub'
        });
    };

    const onRecordPress = () => {
        onModalOpenRequest(playerModalsEnum.recording);
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'record-button',
            context: '/player'
        });
    };

    // const onLanguageSelectionPress = () => {
    //     onModalOpenRequest(playerModalsEnum.audiotracks);
    // };

    const onPlay = () => {
        if (playerInstance?.live && streamType === STREAM_TYPE.LIVE && playerInstance?.paused) {
            const continueOn = Device.isIos ? longPauseFromLive - 46000 : longPauseFromLive;
            if (progressInfo.current) {
                setProgress(progressInfo.current);
            }
            switchToTimeshift(continueOn, SeekType.UTC);
        } else {
            onResumePlaying();
        }
        setLongPauseFromLive(null);
        visibilityToggleIsLocked.current = false;
        postponeVisibilityToggle();
        onPlayPress();
    };

    const onPause = () => {
        if (playerInstance?.live && streamType === STREAM_TYPE.LIVE) {
            const timestamp = playerInstance.getUTCTimestamp(playerInstance.currentTime)?.getTime();
            setLongPauseFromLive(timestamp);
        }
        visibilityToggleIsLocked.current = true;
        playerInstance?.pause();
        if (client) {
            setMediaState((state) => ({
                ...state,
                isPaused: true
            }));
            client.pause();
            onPausePress();
        }
    };

    const setVolumeWithPostpone = (params) => {
        postponeVisibilityToggle();
        setVolume(params);
    };

    const showAlertForSeekingForbiden = () => {
        postponeVisibilityToggle();
        if (lowLatency) {
            logEvent({
                time: Date.now(),
                type: 'user-flow',
                subtype: 'player',
                deviceId: '0000',
                deviceType: 'OTT_TV_ANDROID',
                clientId: '0000',
                ipaddress: '',
                content1: 'll-seek-interaction',
                content2: 'key',
                context: '/player'
            });
            setAlertMessage(t('LOW_LATENCY.LIMITED_FUNCIONALITY_INFO_MSG_MOBILES'));
        } else if (playerInstance?.currentAdBreak !== null) {
            // Todo: ask @skvesh if this is correct solution
            setAlertMessage(mediaHouse ?
                t(`AD_SKIPPING.${mediaHouse}.FORCED_RETURN_TO_WATCH_AD_BLOCK_MSG`) :
                t('AD_SKIPPING.FORCED_RETURN_TO_WATCH_AD_BLOCK_MSG'));
        } else {
            setAlertMessage(
                mediaHouse ?
                    t(`AD_SKIPPING.${mediaHouse}.FORCED_RETURN_TO_WATCH_AD_BLOCK_MSG`) :
                    t('COMMON.SEEKING_FORBIDDEN_ERROR_MSG')
            );
        }
        if (alertTimeout.current) clearTimeout(alertTimeout.current);
        alertTimeout.current = setTimeout(() => {
            setAlertMessage('');
            clearTimeout(alertTimeout.current);
        }, 10000);
    };

    /**
         const showAlertForSeekingForbiden = (channelId = null) => {
             if (lowLatency) {
                 logEvent({
                     time: Date.now(),
                     type: 'user-flow',
                     subtype: 'player',
                     deviceId: '0000',
                     deviceType: 'OTT_TV_ANDROID',
                     clientId: '0000',
                     ipaddress: '',
                     content1: 'll-seek-interaction',
                     content2: 'key',
                     context: '/player'
                 });
                 setAlertMessage(t('LOW_LATENCY.LIMITED_FUNCIONALITY_INFO_MSG_MOBILES'));
             } else if (!playerInstance?.currentAdBreak) {
                 const messageKey = channelId === null
                     ? 'AD_SKIPPING.FORCED_RETURN_TO_WATCH_AD_BLOCK_MSG'
                     : `${channelId}.AD_SKIPPING.FORCED_RETURN_TO_WATCH_AD_BLOCK_MSG`;
                 setAlertMessage(t(messageKey));
             } else {
                 setAlertMessage(t('COMMON.SEEKING_FORBIDDEN_ERROR_MSG'));
             }
             if (alertTimeout.current) clearTimeout(alertTimeout.current);
             alertTimeout.current = setTimeout(() => {
                 setAlertMessage('');
                 clearTimeout(alertTimeout.current);
             }, 10000);
         }; */

    const onSeekBackward = () => {
        postponeVisibilityToggle();
        if (isModalBinge) {
            setModalType(null);
            setLiveLoading(false);
        } else {
            playerInstance?.backward(30000);
        }
        client?.seek({
            position: -30,
            infinite: false,
            relative: true,
            customData: {
                sign: -1
            }
        });
    };

    const onSeekForward = () => {
        postponeVisibilityToggle();
        if (
            streamType === 'LIVE' &&
            playerInstance?.live === false &&
            playerInstance.getUTCTimestamp(playerInstance.currentTime).getTime() <
            Date.now() - 30000
        ) {
            playerInstance?.forward(30000);
            client?.seek({
                position: 30,
                infinite: false,
                relative: true,
                customData: {
                    sign: 1
                }
            });
        } else if (canSeekForwards) {
            playerInstance?.forward(30000);
            client?.seek({
                position: 30,
                infinite: false,
                relative: true,
                customData: {
                    sign: 1
                }
            });
        } else if (currentAd) showAlertForSeekingForbiden();
    };

    const onSeekerDragStart = () => {
        visibilityToggleIsLocked.current = true;
    };

    const onSyntheticSeekerDragEnd = () => {
        console.log('visibility', visibilityToggleIsLocked.current);
        (gesturePosition: number) => {
            console.log('gesturePosition', gesturePosition);
            if (onHorizontalGestureTimeout.current) {
                clearTimeout(onHorizontalGestureTimeout.current);
            }
            if (onHorizontalGestureCallback) {
                onHorizontalGestureTimeout.current = setTimeout(() => {
                    onHorizontalGestureCallback(gesturePosition < 0 ? -1 : 1);
                }, HORIZONTAL_GESTURE_TIMEOUT);
            }
        };
        visibilityToggleIsLocked.current = false;
        postponeVisibilityToggle();
    };

    const onSeekerDragEnd = (event: { end: number; progress: number }) => {
        if (lowLatency) return;
        visibilityToggleIsLocked.current = false;
        postponeVisibilityToggle();
        if (item.modelType !== modelType.tv) return;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore there is wrong type for item
        const assetDurationInSeconds = item.getDuration() / 1000;
        const durationPercentage = assetDurationInSeconds / 100;
        setProgress(false);
        if (
            event.end !== 100 &&
            (event.progress === event.end ||
                event.end * durationPercentage - event.progress * durationPercentage <= 5)
        ) {
            if (streamType === STREAM_TYPE.LIVE) return;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (item.getMetaData()?.detail?.seekingForbidden === true) return;
            setProgress(event);
            switchToLive();
        }
        if (streamType === STREAM_TYPE.LIVE) setProgress(event);
    };

    const keepShowingUI = useCallback(
        (successCallback?: () => unknown) => {
            if (!modal) {
                if (!isVisible) toggleUI();
                postponeVisibilityToggle();
                if (successCallback) successCallback();
            }
        },
        [isVisible, modal, toggleUI]
    );

    const onHorizontalGesture = useCallback(
        (gesturePosition: number) => {
            console.log('gesturePosition', gesturePosition);
            if (onHorizontalGestureTimeout.current) {
                clearTimeout(onHorizontalGestureTimeout.current);
            }
            if (onHorizontalGestureCallback) {
                onHorizontalGestureTimeout.current = setTimeout(() => {
                    onHorizontalGestureCallback(gesturePosition < 0 ? -1 : 1);
                }, HORIZONTAL_GESTURE_TIMEOUT);
            }
        },
        [onHorizontalGestureCallback]
    );

    const onVolumeGesture = useCallback(
        async (value: number) => {
            await setVolume(value);
            keepShowingUI(() => {
                playerInstance.volume = value;
            });
        },
        [keepShowingUI, playerInstance?.volume, setVolume]
    );

    const onBrightnessGesture = useCallback(
        (value: number) => {
            // setBrightness(value);
            keepShowingUI();
        },
        [keepShowingUI]
    );

    // TODO: refactor into separate component
    const getFooterLeftIcons = () => {
        let displayRecordButton = false;
        if (isLiveTv(liveItem)) {
            const { data: recData } = getRecordingForItem(liveItem);
            const hasBeenAlreadyRecorded = Boolean(recData);
            displayRecordButton =
                hasBeenAlreadyRecorded ||
                ('isRecordable' in liveItem &&
                    typeof liveItem?.isRecordable === 'function' &&
                    liveItem?.isRecordable());
        }
        if (isUserFreemium(userData)) displayRecordButton = false;
        if (displayRecordButton) {
            return [
                {
                    key: 'recordIcon',
                    onPress: onRecordPress,
                    name: hasRecordingData(item) ? 'player_record_on' : 'player_record',
                    style: defaultStyles.recIcon
                }
            ];
        }
        return [];
    };

    const onClick = () => {
        const { activeElement } = document;

        if (activeElement && activeElement) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            activeElement.blur();
        }
    };

    const getFooterRightIcons = () =>
        [
            Device.isIos &&
            playerInstance && {
                key: 'AirPlayIcon',
                type: 'AirPlay',
                name: 'AirPlay'
            },
            // audioTracks.length > 1 && {
            //     key: 'settingsIcon',
            //     name: 'nav_settings',
            //     onPress: onSettingsPress
            // },
            Device.isWeb && {
                key: 'fullscreenIcon',
                onPress: toggleFullscreen,
                name: fullscreen ? 'fullscreen_exit' : 'fullscreen_enter'
            }
        ].filter(Boolean);

    const handleFullscreenChange = () => {
        // Update state if triggered natively. It is changed after event
        setTimeout(() => {
            const { fullscreenElement } = document;
            setFullscreen(!!fullscreenElement);
        }, 100);
    };

    const handleKey = useCallback(
        (event) => {
            if (modalContent) return;
            switch (event.key) {
                case 'f':
                    toggleFullscreen();
                    break;
                case 'i':
                    onInfoPress();
                    break;
                case 'ArrowLeft':
                    onSeekBackward();
                    break;
                case 'ArrowRight':
                    onSeekForward();
                    break;
                case 'Escape':
                    if (fullscreen) toggleFullscreen();
                    event.preventDefault();
                    break;
                case ' ':
                    togglePlayPause();
                    showUI();
                    break;
                default:
                    break;
            }
        },
        [
            modalContent,
            toggleFullscreen,
            onInfoPress,
            playerInstance,
            fullscreen,
            togglePlayPause,
            showUI,
            isModalBinge,
            canSeekForwards
        ]
    );

    const bindKeyEvents = useCallback(() => {
        if (Device.isWeb) {
            document.addEventListener('keyup', handleKey);
            document.addEventListener('fullscreenchange', handleFullscreenChange);
            document.addEventListener('click', onClick);
        }
    }, [handleKey]);

    const unbindKeyEvents = useCallback(() => {
        if (Device.isWeb) {
            document.removeEventListener('keyup', handleKey);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('click', onClick);
        }
    }, [handleKey]);

    const onMouseMove = ({ movementX, movementY } = { movementX: 0, movementY: 0 }) => {
        // function is for some reason called every new render on safari,
        // so we need to add some movement threshold here
        if (movementX === 0 && movementY === 0) return;
        const showUi =
            shouldUiBePermanentlyDisplayed(modalType, modalOpened) || (isVisible && !modalOpened);
        if (!showUi) showUI();
        postponeVisibilityToggle();
    };

    const showUi =
        shouldUiBePermanentlyDisplayed(modalType, modalOpened) || (isVisible && !modalOpened);

    useEffect(() => {
        if (isModalBinge) {
            setVisible(false);
        }
    }, [modalType]);

    useEffect(() => {
        if (!Device.isWeb) setVisible(false);
    }, []);

    useEffect(() => {
        if (!isVisible) return;
        refreshThumbnails();
        const timeoutId = setInterval(() => {
            refreshThumbnails();
        }, 10000);

        return () => {
            clearInterval(timeoutId);
        };
    }, [isVisible, playerInstance?.live]);

    useEffect(() => {
        const uiDisplayTimer = setInterval(() => {
            if (
                isVisible &&
                !hasToPostponeVisibilityToggle.current &&
                !visibilityToggleIsLocked.current &&
                !modalContent
            ) {
                toggleUI();
            }
        }, HIDE_PLAYER_UI_TIMEOUT);

        if (playerInstance) {
            playerInstance.addEventListener('volumechange', showUI);
        }
        bindKeyEvents();
        return () => {
            clearInterval(uiDisplayTimer);
            if (playerInstance) {
                playerInstance.removeEventListener('volumechange', showUI);
            }
            unbindKeyEvents();
        };
    }, [bindKeyEvents, isVisible, modalContent, playerInstance, showUI, toggleUI, unbindKeyEvents]);

    const gestureOptions: GesturesData = useMemo(
        () => ({
            // volume: {
            //     value: volume,
            //     callback: onVolumeGesture,
            //     vertical: { from: 0, to: 100 },
            //     horizontal: { from: 50, to: 100 },
            //     direction: GESTURE_DIRECTIONS.VERTICAL,
            //     threshold: 50
            // },
            // brightness: {
            //     value: brightness,
            //     callback: onBrightnessGesture,
            //     vertical: { from: 0, to: 100 },
            //     horizontal: { from: 0, to: 50 },
            //     direction: GESTURE_DIRECTIONS.VERTICAL,
            //     threshold: 50
            // },
            horizontal: {
                callback: onHorizontalGesture,
                vertical: { from: 25, to: 75 },
                horizontal: { from: 0, to: 100 },
                direction: GESTURE_DIRECTIONS.HORIZONTAL,
                threshold: 120
            }
        }),
        [onBrightnessGesture, onHorizontalGesture, onVolumeGesture, volume]
    );

    const hasStreamAds = playerInstance?.adBreaks && playerInstance?.adBreaks.length > 0;

    const onLiveTvPress = () => {
        // handle synthetic cannals seeking to live position
        if (streamType === 'LIVE' && !playerInstance?.live) {
            playerInstance?.seek(Date.now(), SeekType.UTC);
            return;
        }
        switchToLive();
        logEvent({
            type: 'user-flow',
            subtype: 'player',
            content1: 'maxiplayer',
            content2: 'jump-to-live-button',
            context: '/player'
        });
    };
    const hideAlertMessage = () => {
        if (alertTimeout.current) clearTimeout(alertTimeout.current);
        setAlertMessage('');
    };
    const onLowLatencyPress = () => {
        logEvent({
            time: Date.now(),
            type: 'user-flow',
            subtype: 'player',
            deviceId: '0000',
            deviceType: 'OTT_TV_ANDROID',
            clientId: '0000',
            ipaddress: '',
            content1: 'maxiplayer',
            content2: 'low-latency-info',
            context: '/low-latency-popup'
        });
        onModalOpenRequest(playerModalsEnum.lowLatency);
    };
    const onCastingPress = () => {
        if (Device.isAndroid) {
            logEvent({
                type: 'user-flow',
                subtype: 'player',
                content1: 'maxiplayer',
                content2: 'chromecast-button',
                context: '/player'
            });
            onModalOpenRequest(playerModalsEnum.chromecast);
        } else {
            playerInstance.castToDevice(CASTING_TYPE.AIRPLAY);
        }
    };

    const PlayerOverlayWrapper = (
        <View
            style={[
                defaultStyles.container,
                showUi ? defaultStyles.backgroundShadow : defaultStyles.backgroundTransparent,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                { cursor: !showUi ? 'none' : 'auto' }
            ]}
        >
            {isCasting /*
            Player currently doees not support DeviceName
            && playerInstance.state?.airPlayDeviceName
            */ ? (
                    <View style={StyleSheet.absoluteFillObject}>
                        <TvCastOverlay
                        /* castedDeviceName={playerInstance.state?.airPlayDeviceName} */
                        />
                    </View>
                ) : null}
            <View
                style={[defaultStyles.hideable, { opacity: showUi ? 1 : 0 }]}
                pointerEvents={showUi ? 'auto' : 'none'}
            >
                {hasStreamAds && gracePeriodInSeconds && (
                    <Countdown
                        playerInstance={playerInstance}
                        setAlertMessage={setAlertMessage}
                        gracePeriodInSeconds={gracePeriodInSeconds}
                        item={item}
                        alertTimeout={alertTimeout}
                        mediaHouse={mediaHouse}
                    />
                )}
                {showUi && (
                    <>
                        <PlayerHeader
                            title={getAssetTitle(item, TITLE_NUMBERING_EPISODE)}
                            channelLogoUrl={
                                ('getChannelLogoUrl' in item.getMetaData() &&
                                    item.getMetaData().getChannelLogoUrl()) ||
                                undefined
                            }
                            onBack={onBack}
                            style={StyleSheet.flatten([
                                defaultStyles.headerContainer
                                // showActivityIndicator && progress === false ? { opacity: 0 } : {}
                            ])}
                            textTracks={textTracks}
                            audioTracks={audioTracks}
                            selectedAudioTrack={selectedAudioTrack}
                            onMiniPlayerPress={onMiniPlayerPress}
                            onInfoPress={onInfoPress}
                            onTvGuidePress={onTvGuidePress}
                            // onLanguageSelectionPress={onLanguageSelectionPress}
                            onLanguagePress={onLanguagePress}
                            streamType={streamType}
                            onLiveTvPress={onLiveTvPress}
                            item={item}
                            currentTime={currentTime}
                            playerInstance={playerInstance}
                            scale={scale}
                            setScale={setScale}
                            lowLatency={lowLatency}
                            onLowLatencyPress={onLowLatencyPress}
                            onCastingPress={onCastingPress}
                            isCasting={isCasting}
                        />
                        <PlayerControls
                            item={item}
                            hasArchive={archiveAvailable}
                            onNextPress={
                                typeof onNextPress === 'function'
                                    ? () => {
                                        onNextPress();
                                        if (progressInfo.current) {
                                            // MARK: NEXT
                                            setProgress(progressInfo.current);
                                        }
                                    }
                                    : null
                            }
                            onPreviousPress={
                                typeof onPreviousPress === 'function'
                                    ? () => {
                                        onPreviousPress();
                                        if (progressInfo.current) {
                                            setProgress(progressInfo.current);
                                        }
                                    }
                                    : null
                            }
                            currentTime={currentTime}
                            streamType={streamType}
                            playerInstance={playerInstance}
                            isPaused={isPaused}
                            onPause={onPause}
                            onPlay={onPlay}
                            volume={volume}
                            setVolume={setVolumeWithPostpone}
                            style={[
                                defaultStyles.controlContainer
                                // showActivityIndicator ? { opacity: 0 } : {},
                                // modalOpened ? ({ visibility: 'hidden' } as StyleMap) : {}
                            ]}
                            setAlertMessage={setAlertMessage}
                            showActivityIndicator={
                                !!((loading && !modalOpened && modalType) || showActivityIndicator)
                            }
                            modalType={modalType}
                            setModalType={setModalType}
                            isModalBinge={isModalBinge}
                            setLiveLoading={setLiveLoading}
                            alertTimeout={alertTimeout}
                            onSeekBackward={onSeekBackward}
                            onSeekForward={onSeekForward}
                            showAlertForSeekingForbiden={showAlertForSeekingForbiden}
                            canSeekBackwards={canSeekBackwards}
                            canSeekForwards={canSeekForwards}
                            lowLatency={lowLatency}
                        />
                        {castSession && castDevice ? <View style={defaultStyles.footerContainer} /> : (<PlayerFooter
                            maxTimeshift={maxTimeshift}
                            playerInstance={playerInstance}
                            item={item}
                            streamType={streamType}
                            leftIcons={getFooterLeftIcons()}
                            rightIcons={getFooterRightIcons()}
                            onSeekerDragStart={onSeekerDragStart}
                            onSeekerDragEnd={onSeekerDragEnd}
                            onSyntheticSeekerDragEnd={onSyntheticSeekerDragEnd}
                            onDurationPress={onDurationPress}
                            onCurrentTimePress={onCurrentTimePress}
                            style={StyleSheet.flatten([
                                defaultStyles.footerContainer,
                                showActivityIndicator && progress === false ? { opacity: 0 } : {}
                                // showUi ? {} : ({ display: 'none' } as StyleMap)
                            ])}
                            progressReplace={progress}
                            adSkipPolicy={adSkipPolicy}
                        />)}
                    </>
                )}
            </View>
            {/* <Loader color={Theme.primaryActiveColor} /> */}
        </View>
    );

    if (Device.isWeb) {
        return (
            <Pressable
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore onMouseMove
                onMouseMove={onMouseMove}
                onPress={onOverlayPress}
                style={{ flex: 1 }}
            >
                {PlayerOverlayWrapper}
                {Boolean(alertMessage) && (
                    <Alert
                        style={styles.alert}
                        innerContainerStyle={styles.alertInnerContainerStyle}
                        message={alertMessage}
                        disableClicksOnContainer={!!Device.isTabletOrWeb}
                        onPress={hideAlertMessage}
                    />
                )}
            </Pressable>
        );
    }

    return (
        <GestureHandler
            disable={Boolean(modalType && !isModalBinge)}
            onOverlayPress={onOverlayPress}
            showUI={showUI}
            gestureOptions={gestureOptions}
        >
            {PlayerOverlayWrapper}
            {Boolean(alertMessage) && (
                <Alert
                    style={styles.alert}
                    innerContainerStyle={styles.alertInnerContainerStyle}
                    message={alertMessage}
                    disableClicksOnContainer={!!Device.isTabletOrWeb}
                    onPress={hideAlertMessage}
                />
            )}
        </GestureHandler>
    );
};

export default PlayerOverlay;
